import { createElement, ReactNode } from 'react';
import create from 'zustand';
import shallow from 'zustand/shallow';

import { Spinner } from 'components/Icon';

export enum NotificationType {
  LOADING = 'LOADING',
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARN = 'WARN',
  ERROR = 'ERROR',
  CUSTOM = 'CUSTOM'
}

export type NotificationOptions = {
  autoDismiss?: boolean;
  dismissOnClick?: boolean;
  duration?: number;
};

export type Notification = {
  id: string;
  type: NotificationType;
  content: string | ReactNode;
  options?: NotificationOptions;
};

export type NotificationStore = {
  notifications: Map<string, Notification>;
  show(notification: Notification): void;
  close(notificationId: string): void;
  closeAll(): void;
};

export const ICONS: { [key in NotificationType]: null | string | ReactNode } = {
  [NotificationType.LOADING]: createElement(Spinner, { height: 16, width: 16 }),
  [NotificationType.INFO]: 'ℹ️',
  [NotificationType.SUCCESS]: '✅',
  [NotificationType.WARN]: '⚠️',
  [NotificationType.ERROR]: '💥',
  [NotificationType.CUSTOM]: null
};

export const defaultNotificationOptions: NotificationOptions = {
  autoDismiss: true,
  dismissOnClick: true,
  duration: 5_000
};

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: new Map<string, Notification>(),
  show(notification: Notification) {
    const { notifications } = get();
    const newNotifications = new Map(notifications);
    const newNotification: Notification = { ...notification, options: defaultNotificationOptions };

    if (notification.options) {
      Object.keys(notification.options).forEach(key => {
        const option = (notification.options as NotificationOptions)[key];

        if (option !== undefined) {
          newNotification.options = { ...newNotification.options, [key]: option };
        }
      });
    }

    newNotifications.set(notification.id, newNotification);
    set({ notifications: newNotifications });
  },
  close(notificationId: string) {
    const { notifications } = get();
    const newNotifications = new Map(notifications);

    newNotifications.delete(notificationId);
    set({ notifications: newNotifications });
  },
  closeAll() {
    const newNotifications = new Map();

    set({ notifications: newNotifications });
  }
}));

export const useNotification = () => {
  const controls = useNotificationStore(
    store => ({
      notifications: Array.from(store.notifications.values()),
      show: store.show,
      close: store.close,
      closeAll: store.closeAll
    }),
    shallow
  );

  return controls;
};
